import * as React from 'react';
import type {PropsWithChildren} from 'react';
import {cn} from '../lib/cn';
import {Label} from '../label';
import {ScrollArea} from '../scroll-area';
import {ErrorMessage} from '../error-message';
import {BaseCheckbox} from './BaseCheckbox';
import {Checkbox} from './Checkbox';
import type {CheckboxOption} from './types';

const ScrollAreaWrapper = ({
  children,
  scrollable,
  scrollHeight,
}: Pick<CheckboxGroupProps, 'scrollable' | 'scrollHeight'> & PropsWithChildren) => {
  if (!scrollable) {
    return children;
  }

  return (
    <ScrollArea
      className="checkbox-group__scroll-area max-h-96 w-full overflow-hidden"
      style={scrollHeight ? {height: scrollHeight} : undefined}
    >
      {children}
    </ScrollArea>
  );
};

export interface CheckboxGroupProps {
  options: CheckboxOption[];
  value?: string[];
  onChange?: (selectedValues: string[]) => void;
  disabled?: boolean;
  className?: string;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  scrollable?: boolean;
  scrollHeight?: string | number;
  error?: string;
}

export const CheckboxGroup = React.forwardRef<HTMLDivElement, CheckboxGroupProps>(
  (
    {
      options,
      value = [],
      onChange,
      disabled = false,
      className,
      showSelectAll = true,
      selectAllLabel = 'All',
      scrollable,
      scrollHeight,
      error,
      ...props
    },
    ref,
  ) => {
    const selectAllId = React.useId();

    const handleOptionChange = (optionValue: string, checked: boolean) => {
      if (!onChange) return;

      if (checked) {
        onChange([...value, optionValue]);
        return;
      }

      onChange(value.filter(selectedValue => selectedValue !== optionValue));
    };

    const handleSelectAllChange = (checked: boolean) => {
      if (!onChange) return;

      if (checked) {
        onChange(options.filter(option => !option.disabled).map(option => option.value));
        return;
      }

      onChange([]);
    };

    const availableOptions = options.filter(option => !option.disabled);
    const selectedAvailableValues = value.filter(selectedValue =>
      availableOptions.some(option => option.value === selectedValue),
    );
    const isAllSelected =
      selectedAvailableValues.length > 0 &&
      selectedAvailableValues.length === availableOptions.length;

    return (
      <div ref={ref} className={cn('checkbox-group space-y-2', className)} {...props}>
        {showSelectAll && (
          <>
            <div className="checkbox-group__select-all flex items-center gap-2">
              <BaseCheckbox
                id={selectAllId}
                checked={isAllSelected}
                onCheckedChange={checked => handleSelectAllChange(checked === true)}
                disabled={disabled || availableOptions.length === 0}
              />
              <Label
                htmlFor={selectAllId}
                className="checkbox-group__select-all-label cursor-pointer text-sm font-medium leading-none text-[var(--checkbox-label-color)] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {selectAllLabel}
              </Label>
            </div>
            <hr className="checkbox-group__separator border-t border-[var(--chekin-color-gray-3)]" />
          </>
        )}
        <ScrollAreaWrapper scrollable={scrollable} scrollHeight={scrollHeight}>
          <div className="checkbox-group__options space-y-2">
            {options.map(option => (
              <Checkbox
                key={option.value}
                label={option.label}
                value={value.includes(option.value)}
                onChange={checked => handleOptionChange(option.value, checked)}
                disabled={disabled || option.disabled}
              />
            ))}
          </div>
        </ScrollAreaWrapper>
        {error && (
          <ErrorMessage disabled={disabled} className="checkbox-group__error">
            {error}
          </ErrorMessage>
        )}
      </div>
    );
  },
);

CheckboxGroup.displayName = 'CheckboxGroup';
